// The zoom ladder's regimes: which rig draws the Atlas's own frame, the SURFACE
// edge between them, and the eased handover that retires the orrery.

import { track } from '../visits/bus.ts';

/**
 * Which rig draws the Atlas's own frame.
 *
 * `aloft` is every band above SURFACE: the focus camera over the orrery and the
 * pyramid. `surface` is the SURFACE band with no plot mesh to stand on, so
 * still the focus camera. `walkaround` is the SURFACE band on the mesh, drawn
 * from the plot's own rig. Tier rungs are labels on the same altitude, not
 * regimes; cruise is not one either, since the ladder does not tick there.
 */
export type LadderRegime = 'aloft' | 'surface' | 'walkaround';

export function regimeOf(inSurfaceBand: boolean, onMesh: boolean): LadderRegime {
  if (!inSurfaceBand) return 'aloft';
  return onMesh ? 'walkaround' : 'surface';
}

export interface ZoomLadderDeps {
  /** The focus camera's altitude is under the SURFACE edge. */
  inSurfaceBand(): boolean;
  /** The walkaround's rig owns the frame: the band, a mesh, and the ladder drawing. */
  onMesh(): boolean;
  /** Down across the SURFACE edge, into either regime under it. */
  enterSurfaceBand(): void;
  /** Back up across it. */
  leaveSurfaceBand(): void;
  /** After either crossing's own work. */
  crossed(): void;
  /** Draw or retire the orrery, the stars and the world root. */
  showOrrery(on: boolean): void;
  showPlot(on: boolean): void;
  /** `surface-clamp --ascent --inject legacy-fade`: the plot held by the eased blend again. */
  readonly legacyPlotFade: boolean;
  /** The focused body, for the analytics events a crossing reports. */
  bodyId?(): string | null;
}

export interface ZoomLadderHandle {
  /** The regime the last `cross()` decided. */
  readonly regime: LadderRegime;
  /**
   * 0 flying the body's own frame, 1 standing on the claim.
   *
   * The SURFACE edge is blended through this rather than switched: the handover
   * used to sit at the CLAIM edge and hide the globe to reveal a different
   * scene at the world origin, which is a CUT, and the Atlas's whole promise is
   * a fall.
   */
  readonly descent: number;
  /**
   * Whether `descent` has caught up with the regime's own handover target.
   *
   * Not the SURFACE band: a meshless `surface` regime's target is 0, same as
   * `aloft`, so this settles the moment the band is entered rather than
   * reading true for as long as the band holds.
   */
  readonly blending: boolean;
  /** The orrery is on, or the plot has the frame. */
  readonly orreryOn: boolean;
  /** Decide this frame's regime and run a SURFACE crossing's enter or leave. */
  cross(): void;
  /** Ease the handover toward the regime `cross` decided; the orrery flips at its half. */
  ease(dt: number): void;
  /**
   * Called on the change and not on the frame. This used to be a per-frame
   * traversal of about eighty line and dot objects writing `transparent` and
   * `opacity`, which flapped three.js's program cache key around its 0.999
   * threshold every frame of the descent.
   */
  setOrreryVisible(on: boolean): void;
  /** Cruise took the frame, and nothing eases the blend out there. */
  resetBlend(): void;
  /**
   * Return to Ship, LAUNCH: end a SURFACE regime the way a wheel-out over the
   * edge does, rather than leave it for the next `cross()` to notice. Runs
   * the leave hooks once from `surface` or `walkaround` and lands `aloft`
   * with `descent` 0; from `aloft` it only zeroes the blend.
   */
  suspend(): void;
}

export function createZoomLadder(deps: ZoomLadderDeps): ZoomLadderHandle {
  let regime: LadderRegime = 'aloft';
  let descent = 0;
  let orreryOn = true;

  function setOrreryVisible(on: boolean): void {
    orreryOn = on;
    deps.showOrrery(on);
  }

  // Only the SURFACE edge has work of its own. The rig swap between `surface`
  // and `walkaround` is the camera branch reading `onMesh()` and the blend's
  // target, and the plot build's own reveal re-aims the rig.
  function cross(): void {
    const was = regime;
    regime = regimeOf(deps.inSurfaceBand(), deps.onMesh());
    if ((was === 'aloft') === (regime === 'aloft')) return;
    track(regime === 'aloft' ? 'walkaround_end' : 'surface_reached', { body_id: deps.bodyId?.() ?? 'none' });
    if (regime === 'aloft') deps.leaveSurfaceBand();
    else deps.enterSurfaceBand();
    deps.crossed();
  }

  function ease(dt: number): void {
    const want = regime === 'walkaround' ? 1 : 0;
    if (Math.abs(descent - want) > 0.0005) {
      descent += (want - descent) * (1 - Math.exp(-dt * 3.2));
    } else {
      descent = want;
    }
    const e = descent * descent * (3 - 2 * descent); // smoothstep
    const wantOrreryOn = e <= 0.5;
    if (orreryOn !== wantOrreryOn) setOrreryVisible(wantOrreryOn);
    deps.showPlot(deps.legacyPlotFade ? descent > 0.004 : regime !== 'aloft');
  }

  return {
    get regime() { return regime; },
    get descent() { return descent; },
    get blending() { return Math.abs(descent - (regime === 'walkaround' ? 1 : 0)) > 0.0005; },
    get orreryOn() { return orreryOn; },
    cross,
    ease,
    setOrreryVisible,
    resetBlend(): void { descent = 0; },
    suspend(): void {
      const was = regime;
      regime = 'aloft';
      if (was !== 'aloft') {
        track('walkaround_end', { body_id: deps.bodyId?.() ?? 'none' });
        deps.leaveSurfaceBand();
        deps.crossed();
      }
      descent = 0;
    },
  };
}
